import { BaseService } from './base-service';
import { GetListResolution886Mapper } from '@/core/mapper/resolution-886';
import {
	GetListResolution886RequestDTO,
	GetListResolution886ResponseDTO,
	DestroyResolution886ReportRequestDTO,
	DestroyResolution886ReportResponseDTO,
	DownloadFileRequestDTO,
	DownloadFileResponseDTO,
	DownloadPdfRequestDTO,
	DownloadPdfResponseDTO,
	GeneratePDFRequestDTO,
	GeneratePDFResponseDTO,
	CreateResolution886ReportRequestDTO,
	CreateResolution886ReportResponseDTO,
	UpdateResolution886ReportRequestDTO,
	UpdateResolution886ReportResponseDTO,
	GetResolution886ReportRequestDTO,
	GetResolution886ReportResponseDTO,
	GetResolution886ReportReviewRequestDTO,
	GetResolution886ReportReviewResponseDTO,
	DestroyResolution886ReviewRequestDTO,
	DestroyResolution886ReviewResponseDTO,
	GetResolution886AssessmentCategoryResultRequestDTO,
	GetResolution886AssessmentCategoryResultResponseDTO,
	CreateUpdateResolution886CategoryQuestionResponse,
	CreateUpdateResolution886CategoryQuestionParams,
	CreateResolution886ReportAssessmentRequestDTO,
	CreateResolution886ReportAssessmentResponseDTO,
	UpdateResolution886ReportAssessmentRequestDTO,
	UpdateResolution886ReportAssessmentResponseDTO,
	GetResolution886CategoryQuestionRequestDTO,
	GetResolution886CategoryQuestionResponseDTO,
	UpdateResolution886CategoryResultRequestDTO,
	UpdateResolution886CategoryResultResponseDTO,
	CreateResolution886CategoryResultRequestDTO,
	CreateResolution886CategoryResultResponseDTO,
	GetResolution886CategoryResultRequestDTO,
	GetResolution886CategoryResultResponseDTO,
	DestroyAttachmentRequestDTO,
	DestroyAttachmentResponseDTO,
	GetCategoryResultListRequestDTO,
	GetCategoryResultListResponseDTO,
	GetResolution886ReviewResultRequestDTO,
	GetResolution886ReviewResultResponseDTO,
	CreateUpdatePreventiveMeasureRequestDTO,
	CreateUpdatePreventiveMeasureResponseDTO,
	GetPreventiveMeasureRequestDTO,
	GetPreventiveMeasureResponseDTO,
	GetListActionPlanRequestDTO,
	GetListActionPlanResponseDTO,
	GetListFilesRequestDTO,
	GetListFilesResponseDTO,
	SetConsolidatedReviewRequestDTO,
	SetConsolidatedReviewResponseDTO,
	GetDashboardWorstRiskLevelRequestDTO,
	GetDashboardWorstRiskLevelResponseDTO,
	GetDashboardRiskLevelResponseDTO,
	GetDashboardRiskLevelRequestDTO,
	GenerateGeneralPDFRequestDTO,
	GenerateGeneralPDFResponseDTO,
	UpdateStepReportResponseDTO,
	UpdateStepReportRequestDTO,
	GetListGeneralResultRequestDTO,
	GetListGeneralResultResponseDTO,
	GetAttachmentRequestDTO,
	GetAttachmentResponseDTO,
	CreateActionPlanRequestDTO,
	CreateActionPlanResponseDTO,
	UploadAttachmentRequestDTO,
	UploadAttachmentResponseDTO,
	CreateAssessmentAdditionalEvaluationRequestDTO,
	CreateAssessmentAdditionalEvaluationResponseDTO,
	UpdateAssessmentAdditionalEvaluationResponseDTO,
	UpdateAssessmentAdditionalEvaluationRequestDTO
} from '@/core/dto/resolution-886-report';
import type { Pagination } from '@/types';
import type { Resolution886ItemList } from '@/types/Resolution886';

export class Resolution886ReportService extends BaseService<any> {
	private static instance: Resolution886ReportService;

	private constructor(public readonly basePath: string = '/resolution-886-report') {
		super();
	}

	public static getInstance(): Resolution886ReportService {
		if (!Resolution886ReportService.instance) {
			Resolution886ReportService.instance = new Resolution886ReportService();
		}
		return Resolution886ReportService.instance;
	}

	public async index(params: GetListResolution886RequestDTO): Promise<Pagination<Resolution886ItemList>> {
		const url = this.basePath + '/';
		const { data } = await this.getInstance().get<GetListResolution886ResponseDTO>(url, { params });
		return new GetListResolution886Mapper(data).toDomain();
	}

	public async destroy(params: DestroyResolution886ReportRequestDTO): Promise<DestroyResolution886ReportResponseDTO> {
		const { organization_id, company_id, id } = params;
		const url = this.basePath + `/${id}`;
		const { data } = await this.getInstance().delete<DestroyResolution886ReportResponseDTO>(url, {
			params: { organization_id, company_id }
		});
		return data;
	}

	public async downloadPdf(params: DownloadPdfRequestDTO): Promise<string> {
		const { organization_id, company_id, file_id, custom_report_result_id, locale, report_id, exclude_fields } =
			params;
		const url = this.basePath + `/${custom_report_result_id}/download`;
		const { data } = await this.getInstance().get<DownloadPdfResponseDTO>(url, {
			params: { organization_id, company_id, file_id, locale, report_id, exclude_fields }
		});
		return data.url;
	}

	public async downloadFile(params: DownloadFileRequestDTO): Promise<DownloadFileResponseDTO> {
		const { organization_id, company_id, file_id, original_name } = params;
		const url = this.basePath + `/${file_id}/download`;
		const { data } = await this.getInstance().get<DownloadFileResponseDTO>(url, {
			params: { organization_id, company_id, original_name }
		});
		return data;
	}

	public async create(params: CreateResolution886ReportRequestDTO): Promise<CreateResolution886ReportResponseDTO> {
		const url = this.basePath + '/';
		const { data } = await this.getInstance().post<CreateResolution886ReportResponseDTO>(url, params);
		return data;
	}

	public async update(params: UpdateResolution886ReportRequestDTO): Promise<UpdateResolution886ReportResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await this.getInstance().put<UpdateResolution886ReportResponseDTO>(url, params);
		return data;
	}

	public async updateReviewConsolidated(
		params: UpdateResolution886ReportRequestDTO
	): Promise<UpdateResolution886ReportResponseDTO> {
		const url = this.basePath + `/${params.review_id}`;
		const { data } = await this.getInstance().put<UpdateResolution886ReportResponseDTO>(url, params);
		return data;
	}

	public async show(params: GetResolution886ReportRequestDTO): Promise<GetResolution886ReportResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await this.getInstance().get<GetResolution886ReportResponseDTO>(url, { params });
		return data;
	}

	public async createAssessment(
		params: CreateResolution886ReportAssessmentRequestDTO
	): Promise<CreateResolution886ReportAssessmentResponseDTO> {
		const url = this.basePath + '/assessment';
		const { data } = await this.getInstance().post<CreateResolution886ReportAssessmentResponseDTO>(url, params);
		return data;
	}

	public async updateAssessment(
		params: UpdateResolution886ReportAssessmentRequestDTO
	): Promise<UpdateResolution886ReportAssessmentResponseDTO> {
		const url = this.basePath + `/assessment/${params.assessment_id}`;
		const { data } = await this.getInstance().put<UpdateResolution886ReportAssessmentResponseDTO>(url, params);
		return data;
	}

	public async getReview(
		params: GetResolution886ReportReviewRequestDTO
	): Promise<GetResolution886ReportReviewResponseDTO> {
		const url = this.basePath + `/review/${params.review_id}`;
		const { data } = await this.getInstance().get<GetResolution886ReportReviewResponseDTO>(url, { params });
		return data;
	}

	public async destroyReview(
		params: DestroyResolution886ReviewRequestDTO
	): Promise<DestroyResolution886ReviewResponseDTO> {
		const url = this.basePath + `/review/${params.review_id}`;
		const { data } = await this.getInstance().delete<DestroyResolution886ReviewResponseDTO>(url, { params });
		return data;
	}

	public async getAssessmentCategoryResult(
		params: GetResolution886AssessmentCategoryResultRequestDTO
	): Promise<GetResolution886AssessmentCategoryResultResponseDTO> {
		const url = this.basePath + `/review/assessment/category-result/${params.review_id}`;
		const { data } = await this.getInstance().get<GetResolution886AssessmentCategoryResultResponseDTO>(url, {
			params
		});
		return data;
	}

	public async createUpdateCategoryQuestion(
		params: CreateUpdateResolution886CategoryQuestionParams
	): Promise<CreateUpdateResolution886CategoryQuestionResponse> {
		const url = this.basePath + `/assessment/category/step/${params.assessment_id}`;
		const { data } = await this.getInstance().post<CreateUpdateResolution886CategoryQuestionResponse>(url, params);
		return data;
	}

	public async getCategoryQuestion(
		params: GetResolution886CategoryQuestionRequestDTO
	): Promise<GetResolution886CategoryQuestionResponseDTO> {
		const url = this.basePath + `/assessment/category/step/result`;
		const { data } = await this.getInstance().get<GetResolution886CategoryQuestionResponseDTO>(url, { params });
		return data;
	}

	public async getCategoryResult(
		params: GetResolution886CategoryResultRequestDTO
	): Promise<GetResolution886CategoryResultResponseDTO> {
		const url = this.basePath + `/assessment/category/${params.assessment_id}`;
		const { data } = await this.getInstance().get<GetResolution886CategoryResultResponseDTO>(url, { params });
		return data;
	}

	public async createCategoryResult(
		params: CreateResolution886CategoryResultRequestDTO
	): Promise<CreateResolution886CategoryResultResponseDTO> {
		const url = this.basePath + `/assessment/category/${params.assessment_id}`;
		const { data } = await this.getInstance().post<CreateResolution886CategoryResultResponseDTO>(url, params);
		return data;
	}

	public async updateCategoryResult(
		params: UpdateResolution886CategoryResultRequestDTO
	): Promise<UpdateResolution886CategoryResultResponseDTO> {
		const url = this.basePath + `/assessment/category/${params.assessment_id}`;
		const { data } = await this.getInstance().put<UpdateResolution886CategoryResultResponseDTO>(url, params);
		return data;
	}

	public async createActionPlan(params: CreateActionPlanRequestDTO): Promise<CreateActionPlanResponseDTO> {
		const url = this.basePath + '/action-plan';
		const { data } = await this.getInstance().post<CreateActionPlanResponseDTO>(url, params);
		return data;
	}

	public async uploadAttachment(params: UploadAttachmentRequestDTO): Promise<UploadAttachmentResponseDTO> {
		const url = this.basePath + '/action-plan/attachments/upload';
		const { data } = await this.getInstance().post<UploadAttachmentResponseDTO>(url, params);
		return data;
	}

	public async getAttachments(params: GetAttachmentRequestDTO): Promise<GetAttachmentResponseDTO[]> {
		const url = this.basePath + '/action-plan/attachments';
		const { data } = await this.getInstance().get<GetAttachmentResponseDTO[]>(url, { params });
		return data;
	}

	public async destroyAttachment(params: DestroyAttachmentRequestDTO): Promise<DestroyAttachmentResponseDTO> {
		const { id, ...payload } = params;
		const url = this.basePath + `/action-plan/attachment/${id}`;
		const { data } = await this.getInstance().delete<DestroyAttachmentResponseDTO>(url, {
			params: payload
		});
		return data;
	}

	public async getCategoryResultList(
		params: GetCategoryResultListRequestDTO
	): Promise<GetCategoryResultListResponseDTO> {
		const url = this.basePath + `/review/assessment/category/result/action-plan/${params.review_id}`;
		const { data } = await this.getInstance().get<GetCategoryResultListResponseDTO>(url, { params });
		return data;
	}

	public async generatePDF(params: GeneratePDFRequestDTO): Promise<GeneratePDFResponseDTO> {
		const url = this.basePath + `/review/download-pdf/${params.review_id}`;
		const { data } = await this.getInstance().post<GeneratePDFResponseDTO>(url, params);
		return data;
	}

	public async getReviewResult(
		params: GetResolution886ReviewResultRequestDTO
	): Promise<GetResolution886ReviewResultResponseDTO> {
		const url = this.basePath + `/review/result/${params.review_id}`;
		const { data } = await this.getInstance().get<GetResolution886ReviewResultResponseDTO>(url, { params });
		return data;
	}

	public async createUpdatePreventiveMeasure(
		params: CreateUpdatePreventiveMeasureRequestDTO
	): Promise<CreateUpdatePreventiveMeasureResponseDTO> {
		const url = this.basePath + `/review/preventive-measures/${params.review_id}`;
		const { data } = await this.getInstance().post<CreateUpdatePreventiveMeasureResponseDTO>(url, params);
		return data;
	}

	public async getPreventiveMeasure(
		params: GetPreventiveMeasureRequestDTO
	): Promise<GetPreventiveMeasureResponseDTO[]> {
		const url = this.basePath + `/review/preventive-measures/${params.review_id}`;
		const { data } = await this.getInstance().get<GetPreventiveMeasureResponseDTO[]>(url, { params });
		return data;
	}

	public async getListActionPlan(params: GetListActionPlanRequestDTO): Promise<GetListActionPlanResponseDTO> {
		const url = this.basePath + `/review/action-plans/${params.review_id}`;
		const { data } = await this.getInstance().get<GetListActionPlanResponseDTO>(url, { params });
		return data;
	}

	public async getListFiles(params: GetListFilesRequestDTO): Promise<GetListFilesResponseDTO> {
		const url = this.basePath + `/files`;
		const { data } = await this.getInstance().get<GetListFilesResponseDTO>(url, { params });
		return data;
	}

	public async setConsolidatedReview(
		params: SetConsolidatedReviewRequestDTO
	): Promise<SetConsolidatedReviewResponseDTO> {
		const url = this.basePath + '/review/consolidated';
		const { data } = await this.getInstance().post<SetConsolidatedReviewResponseDTO>(url, params);
		return data;
	}

	public async getDashboardWorstRiskLevel(
		params: GetDashboardWorstRiskLevelRequestDTO
	): Promise<GetDashboardWorstRiskLevelResponseDTO[]> {
		const url = this.basePath + '/dashboard/worst-risk-level';
		const { data } = await this.getInstance().get<GetDashboardWorstRiskLevelResponseDTO[]>(url, { params });
		return data;
	}

	public async getDashboardRiskLevel(
		params: GetDashboardRiskLevelRequestDTO
	): Promise<GetDashboardRiskLevelResponseDTO[]> {
		const url = this.basePath + '/dashboard/risk-level';
		const { data } = await this.getInstance().get<GetDashboardRiskLevelResponseDTO[]>(url, { params });
		return data;
	}
	public async updateStep(params: UpdateStepReportRequestDTO): Promise<UpdateStepReportResponseDTO> {
		const url = this.basePath + '/review/set-step';
		const { data } = await this.getInstance().post<UpdateStepReportResponseDTO>(url, params);
		return data;
	}

	public async getListGeneralResult(
		params: GetListGeneralResultRequestDTO
	): Promise<GetListGeneralResultResponseDTO> {
		const url = this.basePath + `/general-result/${params.report_id}`;
		const { data } = await this.getInstance().get<GetListGeneralResultResponseDTO>(url, {
			params
		});
		return data;
	}

	public async createAssessmentAdditionalEvaluation(
		params: CreateAssessmentAdditionalEvaluationRequestDTO
	): Promise<CreateAssessmentAdditionalEvaluationResponseDTO> {
		const url = this.basePath + '/assessment/additional-evaluation';
		const { data } = await this.getInstance().post<CreateAssessmentAdditionalEvaluationResponseDTO>(url, params);
		return data;
	}

	public async updateAssessmentAdditionalEvaluation(
		params: UpdateAssessmentAdditionalEvaluationRequestDTO
	): Promise<UpdateAssessmentAdditionalEvaluationResponseDTO> {
		const url = this.basePath + '/assessment/additional-evaluation';
		const { data } = await this.getInstance().put<UpdateAssessmentAdditionalEvaluationResponseDTO>(url, params);
		return data;
	}

	public async downloadGeneralPDF(params: GenerateGeneralPDFRequestDTO): Promise<GenerateGeneralPDFResponseDTO> {
		const url = this.basePath + `/general-result/download-pdf/${params.report_id}`;
		const { data } = await this.getInstance().post<GenerateGeneralPDFResponseDTO>(url, params);
		return data;
	}
}
